import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { PRODUCTS, OAUTH_NOTE, APP_COLLECTIONS, WRITE_BACK, INTEGRATION_RULES, ENTITY_BLURBS } from '../data/zohoMapData';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  ReceiptText, Network, ClipboardList, Database, KeyRound, ArrowDownToLine, ArrowUpFromLine,
  Radio, ChevronRight, Filter, Route, GitBranch, Hash, ExternalLink, Zap,
  Target, CheckCircle2, ArrowRight, ArrowLeft, ArrowUpRight,
} from 'lucide-react';

const ACCENT = {
  blue: { icon: 'text-blue-600', soft: 'bg-blue-50', softBorder: 'border-blue-100', dot: 'bg-blue-500', chip: 'bg-blue-50 text-blue-700 border-blue-200', bar: 'bg-blue-500', link: 'text-blue-600' },
  violet: { icon: 'text-violet-600', soft: 'bg-violet-50', softBorder: 'border-violet-100', dot: 'bg-violet-500', chip: 'bg-violet-50 text-violet-700 border-violet-200', bar: 'bg-violet-500', link: 'text-violet-600' },
  emerald: { icon: 'text-emerald-600', soft: 'bg-emerald-50', softBorder: 'border-emerald-100', dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', bar: 'bg-emerald-500', link: 'text-emerald-600' },
};

const PRODUCT_ICON = { books: ReceiptText, crm: Network, forms: ClipboardList };

const DIR = {
  PULL: { cls: 'bg-blue-50 text-blue-700 border-blue-200', Icon: ArrowDownToLine, label: 'PULL' },
  PUSH: { cls: 'bg-rose-50 text-rose-700 border-rose-200', Icon: ArrowUpFromLine, label: 'PUSH' },
  INBOUND: { cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: Radio, label: 'INBOUND' },
  BOTH: { cls: 'bg-violet-50 text-violet-700 border-violet-200', Icon: GitBranch, label: 'PULL + PUSH' },
};

function DirBadge({ dir, size = 'sm' }) {
  const m = DIR[dir] || DIR.PULL;
  const { Icon } = m;
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border font-bold tracking-wide ${m.cls} ${size === 'lg' ? 'text-[11px] px-2.5 py-1' : 'text-[10px] px-2 py-0.5'}`} data-testid={`dir-badge-${dir}`}>
      <Icon className="w-3 h-3" />{m.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return null; }
}
function fmtShort(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return '—'; }
}

function PageChips({ pages, onNavigate, max = 0 }) {
  const shown = max ? pages.slice(0, max) : pages;
  const extra = max ? pages.length - shown.length : 0;
  return (
    <div className="flex flex-wrap gap-1.5">
      {shown.map((p, i) => {
        const navigable = !p.slug.includes(':');
        return (
          <button
            key={i}
            type="button"
            disabled={!navigable}
            onClick={(e) => { e.stopPropagation(); if (navigable) onNavigate(p.slug); }}
            data-testid={`page-chip-${p.slug.replace(/[^a-z0-9]/gi, '-')}`}
            title={navigable ? `Go to ${p.slug}` : `${p.slug} (needs a record id)`}
            className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-medium transition-colors ${navigable ? 'bg-white border-slate-200 text-slate-700 hover:bg-violet-50 hover:border-violet-300 hover:text-violet-700 cursor-pointer' : 'bg-slate-50 border-slate-200 text-slate-400 cursor-default'}`}
          >
            {p.label}{navigable && <ArrowUpRight className="w-3 h-3" />}
          </button>
        );
      })}
      {extra > 0 && (
        <span className="inline-flex items-center rounded-lg border border-dashed border-slate-300 px-2 py-1 text-[11px] text-slate-400">+{extra} more</span>
      )}
    </div>
  );
}

/* ---------- Hero flow pieces ---------- */
function FlowCard({ product }) {
  const a = ACCENT[product.accent];
  const Icon = PRODUCT_ICON[product.id] || Database;
  return (
    <div className={`flex-1 min-w-0 bg-white border ${a.softBorder} rounded-2xl p-4 shadow-sm`} data-testid={`flow-card-${product.id}`}>
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`w-10 h-10 rounded-xl ${a.soft} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${a.icon}`} />
        </div>
        <div className="min-w-0">
          <h3 className="font-heading font-bold text-[15px] text-slate-900 leading-tight truncate">{product.name}</h3>
          <p className="text-[11px] text-slate-500 truncate">{product.tag}</p>
        </div>
      </div>
      <ul className="space-y-1.5 mb-3">
        {product.bullets.map((b) => (
          <li key={b} className="flex items-center gap-2 text-[12.5px] text-slate-700">
            <span className={`w-1.5 h-1.5 rounded-full ${a.dot}`} />{b}
          </li>
        ))}
      </ul>
      <span className={`inline-block text-[10px] font-bold tracking-wider rounded-md px-2.5 py-1 ${a.chip} border`}>{product.badge}</span>
    </div>
  );
}

function Arrow({ label, sub, color, dir = 'right' }) {
  const colorMap = { blue: 'text-blue-600', rose: 'text-rose-500', amber: 'text-amber-600' };
  const Icon = dir === 'left' ? ArrowLeft : ArrowRight;
  return (
    <div className="flex flex-col items-center justify-center px-1 py-2 xl:py-0 flex-shrink-0">
      <span className={`text-[11px] font-bold ${colorMap[color]}`}>{label}</span>
      <div className={`flex items-center ${colorMap[color]} my-0.5`}>
        <span className="hidden xl:block w-6 h-px bg-current opacity-40" />
        <Icon className="w-4 h-4 xl:mx-0 rotate-90 xl:rotate-0" />
        <span className="hidden xl:block w-6 h-px bg-current opacity-40" />
      </div>
      {sub && <span className="text-[9.5px] text-slate-400 text-center leading-tight max-w-[90px]">{sub}</span>}
    </div>
  );
}

function AppHub() {
  return (
    <div className="flex-[1.3] min-w-0 bg-emerald-50/40 border border-emerald-100 rounded-2xl p-4 shadow-sm" data-testid="app-hub-card">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <Database className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="min-w-0">
          <h3 className="font-heading font-bold text-[15px] text-slate-900 leading-tight">Your Application</h3>
          <p className="text-[11px] text-slate-500">MongoDB (Local Cache)</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-1.5 mb-2">
        {APP_COLLECTIONS.map((c) => (
          <span key={c} className="text-[11px] font-mono text-slate-600 bg-white border border-slate-200 rounded-lg px-2 py-1.5 truncate text-center">{c}</span>
        ))}
      </div>
      <p className="text-[10.5px] text-slate-400 text-center">Stores, processes and powers your application</p>
    </div>
  );
}

/* ---------- Summary card ---------- */
function SummaryCard({ product, onSelect }) {
  const a = ACCENT[product.accent];
  const Icon = PRODUCT_ICON[product.id] || Database;
  const count = product.entities.length;
  return (
    <button
      type="button"
      onClick={() => onSelect(product.id)}
      data-testid={`summary-${product.id}`}
      className="text-left bg-white border border-slate-200 rounded-2xl p-5 shadow-sm transition-all duration-150 hover:border-violet-300 hover:shadow-md hover:-translate-y-0.5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex gap-3 min-w-0">
          <div className={`w-11 h-11 rounded-xl ${a.soft} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${a.icon}`} />
          </div>
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-[15px] text-slate-900">{product.name}</h3>
            <p className="text-[12px] text-slate-500 leading-relaxed mt-0.5">{product.summary}</p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className={`text-3xl font-black leading-none ${a.icon}`}>{count}</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mt-1">Touchpoint{count > 1 ? 's' : ''}</p>
        </div>
      </div>
      <span className={`inline-flex items-center gap-1 text-[12px] font-semibold mt-4 ${a.link}`}>
        View all endpoints <ArrowRight className="w-3.5 h-3.5" />
      </span>
    </button>
  );
}

/* ---------- Endpoint list row ---------- */
function EndpointRow({ entity, product, stat, onOpen, onNavigate }) {
  const a = ACCENT[product.accent];
  const Icon = PRODUCT_ICON[product.id] || Database;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      data-testid={`entity-row-${entity.key}`}
      className="group w-full text-left flex items-start gap-4 px-4 py-3.5 hover:bg-slate-50 transition-colors duration-150 rounded-xl cursor-pointer"
    >
      <div className={`w-10 h-10 rounded-xl ${a.soft} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 ${a.icon}`} />
      </div>
      <div className="min-w-0 flex-1">
        <span className="font-semibold text-[13.5px] text-slate-900">{entity.name}</span>
        <p className="text-[11px] font-mono text-slate-400 mt-0.5 truncate">
          <span className="font-bold text-slate-500">{entity.method}</span> {entity.endpoints[0].split('  ')[0]}
        </p>
        <p className="text-[11.5px] text-slate-500 mt-0.5">{ENTITY_BLURBS[entity.key] || entity.storedIn}</p>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex-shrink-0">Used on</span>
          <PageChips pages={entity.pages} onNavigate={onNavigate} max={3} />
        </div>
      </div>
      <div className="flex flex-col items-end gap-2 flex-shrink-0">
        <DirBadge dir={entity.direction} />
        <div className="hidden md:block text-right">
          {stat?.records !== null && stat?.records !== undefined && (
            <p className="text-[12px] font-semibold text-slate-700">{stat.records.toLocaleString('en-IN')} records</p>
          )}
          <p className="text-[10.5px] text-slate-400">Last sync: {fmtShort(stat?.last_sync)}</p>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0 mt-1" />
    </div>
  );
}

/* ---------- Detail modal ---------- */
function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5 text-slate-400" />
        <h4 className="text-[10px] font-bold tracking-[0.16em] uppercase text-slate-500">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function EntityModal({ selected, stat, onClose, onNavigate }) {
  const entity = selected?.entity;
  const product = selected?.product;
  const a = ACCENT[product?.accent || 'violet'];
  const Icon = product ? (PRODUCT_ICON[product.id] || Database) : Database;
  const isPush = entity?.direction === 'PUSH';
  return (
    <Dialog open={!!entity} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden rounded-2xl" data-testid="entity-modal">
        {entity && (
          <>
            <div className={`${a.soft} border-b ${a.softBorder} px-6 py-5`}>
              <DialogHeader className="space-y-0">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-white flex items-center justify-center border ${a.softBorder}`}>
                    <Icon className={`w-5 h-5 ${a.icon}`} />
                  </div>
                  <DialogTitle className="text-xl font-heading font-bold tracking-tight text-slate-900">{entity.name}</DialogTitle>
                  <DirBadge dir={entity.direction} size="lg" />
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] font-mono text-slate-500">
                  {entity.endpoints.map((e, i) => (
                    <span key={i} className="inline-flex items-center gap-1"><Route className="w-3 h-3" />{entity.method} {e}</span>
                  ))}
                </div>
              </DialogHeader>
            </div>

            <ScrollArea className="max-h-[65vh]">
              <div className="px-6 py-5 space-y-6">
                {/* Prominent "where used" band */}
                <div className="bg-violet-50/60 border border-violet-100 rounded-xl p-4" data-testid="modal-pages-band">
                  <div className="flex items-center gap-2 mb-2.5">
                    <ExternalLink className="w-4 h-4 text-violet-600" />
                    <h4 className="text-[11px] font-bold tracking-[0.14em] uppercase text-violet-700">Where this data appears</h4>
                    <span className="text-[11px] text-slate-400">· {entity.pages.length} page{entity.pages.length > 1 ? 's' : ''}</span>
                  </div>
                  <PageChips pages={entity.pages} onNavigate={onNavigate} max={0} />
                </div>

                {stat && (stat.records !== null || stat.last_sync) && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Records local</p>
                      <p className="font-mono text-lg font-bold text-slate-900">{stat.records?.toLocaleString('en-IN') ?? '—'}</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Last sync</p>
                      <p className="font-mono text-sm font-semibold text-slate-800 mt-1">{fmtShort(stat.last_sync)}</p>
                    </div>
                    {stat.extra && Object.entries(stat.extra).filter(([, v]) => v !== null && v !== undefined).slice(0, 1).map(([k, v]) => (
                      <div key={k} className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{k.replace(/_/g, ' ')}</p>
                        <p className="font-mono text-lg font-bold text-slate-900">{typeof v === 'number' ? v.toLocaleString('en-IN') : v}</p>
                      </div>
                    ))}
                  </div>
                )}

                <Section icon={Filter} title="Conditions applied">
                  <ul className="space-y-1.5">
                    {entity.conditions.map((c, i) => (
                      <li key={i} className="flex items-start gap-2 text-[13px] text-slate-700">
                        <span className={`mt-1.5 w-1 h-1 rounded-full flex-shrink-0 ${a.dot}`} />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </Section>

                <Section icon={Hash} title="API call volume">
                  <p className="text-[13px] text-slate-700 font-mono bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">{entity.callMath}</p>
                </Section>

                <Section icon={Database} title={isPush ? 'Fields pushed to Zoho' : 'Fields pulled from Zoho'}>
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full text-[12px]">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-left">
                          <th className="px-3 py-2 font-bold text-[10px] uppercase tracking-wider text-slate-500">{isPush ? 'CRM field written' : 'Zoho field'}</th>
                          <th className="px-3 py-2 font-bold text-[10px] uppercase tracking-wider text-slate-500">{isPush ? 'Value / source' : 'Stored locally as'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entity.fields.map((f, i) => (
                          <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                            <td className="px-3 py-2 font-mono text-slate-800 align-top">{f.zoho}</td>
                            <td className="px-3 py-2 font-mono text-slate-600 align-top">
                              {f.local}
                              {f.note && <span className="block text-[10px] text-slate-400 font-sans italic mt-0.5">{f.note}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-500">
                    {isPush ? <ArrowUpFromLine className="w-3 h-3" /> : <ArrowDownToLine className="w-3 h-3" />}
                    <span className="font-mono">{isPush ? 'Source' : 'Destination'}: {entity.storedIn}</span>
                  </div>
                  {entity.source && (
                    <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500">
                      <GitBranch className="w-3 h-3" /><span className="font-mono">{entity.source}</span>
                    </div>
                  )}
                </Section>

                <Section icon={Zap} title="Triggered from">
                  <ul className="space-y-1">
                    {entity.triggeredFrom.map((t, i) => (<li key={i} className="text-[12px] text-slate-700">• {t}</li>))}
                  </ul>
                </Section>
              </div>
            </ScrollArea>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Page ---------- */
const TABS = [
  { id: 'all', label: 'All' },
  { id: 'books', label: 'Books' },
  { id: 'crm', label: 'CRM' },
  { id: 'forms', label: 'Forms' },
];

export default function ZohoIntegrationMap() {
  const navigate = useNavigate();
  const endpointsRef = useRef(null);
  const [stats, setStats] = useState({});
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('all');

  const selectProduct = (id) => {
    setTab(id);
    setTimeout(() => endpointsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  };

  const loadStats = () => {
    api.get('/zoho-map/stats').then((r) => setStats(r.data.stats || {})).catch(() => {});
  };
  useEffect(() => { loadStats(); }, []);

  const totals = useMemo(() => {
    let pull = 0, push = 0, inbound = 0;
    PRODUCTS.forEach((p) => p.entities.forEach((e) => {
      if (e.direction === 'PUSH') push += 1;
      else if (e.direction === 'INBOUND') inbound += 1;
      else pull += 1;
    }));
    return { pull, push, inbound, all: pull + push + inbound };
  }, []);

  const allRows = useMemo(() => PRODUCTS.flatMap((p) => p.entities.map((e) => ({ entity: e, product: p }))), []);
  const rows = tab === 'all' ? allRows : allRows.filter((r) => r.product.id === tab);
  const counts = useMemo(() => ({
    all: allRows.length,
    books: allRows.filter((r) => r.product.id === 'books').length,
    crm: allRows.filter((r) => r.product.id === 'crm').length,
    forms: allRows.filter((r) => r.product.id === 'forms').length,
  }), [allRows]);

  const lastBooks = stats.books_invoices?.last_sync;
  const lastCrm = stats.crm_batches?.last_sync;

  const flowProduct = (id) => PRODUCTS.find((p) => p.id === id);

  const syncRows = [
    { id: 'books', name: 'Zoho Books', last: lastBooks, status: 'Success', accent: 'blue' },
    { id: 'crm', name: 'Zoho CRM', last: lastCrm, status: 'Success', accent: 'violet' },
    { id: 'forms', name: 'Zoho Forms', last: stats.crm_placement_pull?.last_sync || lastBooks, status: 'Receiving', accent: 'emerald' },
  ];

  return (
    <div className="space-y-6" data-testid="zoho-integration-map">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-violet-600">Integration Atlas</span>
          <h1 className="text-4xl font-heading font-black tracking-tighter text-slate-950 mt-1">Zoho Integration Map</h1>
          <p className="text-slate-600 mt-2 max-w-2xl text-[13.5px] leading-relaxed">
            A complete view of how data flows between your application and Zoho — what's pulled, what's pushed, and what's stored locally.
          </p>
        </div>
        <div className="flex flex-col items-stretch lg:items-end gap-3 flex-shrink-0">
          <div className="flex items-center gap-2 flex-wrap lg:justify-end">
            <div className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-xl px-3.5 py-2 shadow-sm" data-testid="health-pill">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
              </span>
              <div className="leading-tight">
                <p className="text-[12px] font-bold text-slate-900">All Integrations Healthy</p>
                <p className="text-[10.5px] text-slate-400">Last updated {fmtShort(lastBooks) }</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2.5 flex-wrap lg:justify-end">
            {[
              { n: totals.pull, l: 'Pull Endpoints', Icon: ArrowDownToLine, cls: 'text-blue-600 bg-blue-50 border-blue-100' },
              { n: totals.push, l: 'Push Endpoint', Icon: ArrowUpFromLine, cls: 'text-rose-500 bg-rose-50 border-rose-100' },
              { n: totals.inbound, l: 'Inbound Webhook', Icon: Radio, cls: 'text-amber-600 bg-amber-50 border-amber-100' },
            ].map((s) => (
              <div key={s.l} className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-xl px-3.5 py-2 shadow-sm">
                <div className={`w-8 h-8 rounded-lg border flex items-center justify-center ${s.cls}`}><s.Icon className="w-4 h-4" /></div>
                <div className="leading-tight">
                  <p className="text-lg font-black text-slate-900">{s.n}</p>
                  <p className="text-[10px] text-slate-500 font-medium whitespace-nowrap">{s.l}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Hero flow diagram */}
      <div className="bg-slate-50/60 border border-slate-200 rounded-2xl p-4 md:p-6" data-testid="flow-diagram">
        <div className="flex flex-col xl:flex-row xl:items-stretch gap-3">
          <FlowCard product={flowProduct('books')} />
          <Arrow label="Pull" sub="Financial data" color="blue" dir="right" />
          <AppHub />
          <div className="flex flex-col justify-center gap-3 flex-shrink-0">
            <Arrow label="Pull" sub="Operational data" color="blue" dir="left" />
            <Arrow label="Push" sub="Placement updates (Lead fields)" color="rose" dir="right" />
          </div>
          <FlowCard product={flowProduct('crm')} />
          <Arrow label="Inbound" sub="Webhook submissions" color="amber" dir="left" />
          <FlowCard product={flowProduct('forms')} />
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {PRODUCTS.map((p) => <SummaryCard key={p.id} product={p} onSelect={selectProduct} />)}
      </div>

      {/* Write-back banner */}
      <div className="bg-rose-50/70 border border-rose-200 rounded-2xl p-5" data-testid="writeback-banner">
        <div className="flex flex-col xl:flex-row xl:items-center gap-5">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0">
              <Target className="w-5 h-5 text-rose-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[12px] font-bold tracking-wider uppercase text-rose-700">{WRITE_BACK.title}</p>
              <p className="text-[13px] text-slate-600 mt-1">{WRITE_BACK.detail}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-[12px] font-semibold text-slate-700">{WRITE_BACK.from}</span>
            <div className="flex flex-col items-center text-rose-500">
              <span className="text-[10px] font-mono">{WRITE_BACK.via}</span>
              <ArrowRight className="w-4 h-4" />
            </div>
            <span className="inline-flex items-center gap-2 bg-white border border-violet-200 rounded-xl px-3 py-2">
              <Network className="w-4 h-4 text-violet-600" />
              <span className="leading-tight">
                <span className="block text-[12px] font-semibold text-slate-800">Zoho CRM</span>
                <span className="block text-[10px] text-slate-400">Lead</span>
              </span>
            </span>
          </div>
          <div className="flex-shrink-0 xl:border-l xl:border-rose-200 xl:pl-5">
            <p className="text-[11px] font-bold text-violet-700 mb-1.5">Eligibility Conditions</p>
            <div className="space-y-1">
              {WRITE_BACK.conditions.map((c) => (
                <p key={c} className="flex items-center gap-1.5 text-[12.5px] text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-500" />{c}</p>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Endpoints + side panels */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
        {/* Recent endpoints */}
        <div ref={endpointsRef} className="xl:col-span-2 bg-white border border-slate-200 rounded-2xl shadow-sm scroll-mt-6" data-testid="endpoints-panel">
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 flex-wrap">
            <h2 className="font-heading font-bold text-lg text-slate-900">Recent Endpoints</h2>
            <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`text-[12px] font-semibold rounded-lg px-3 py-1.5 transition-colors ${tab === t.id ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                >
                  {t.label} ({counts[t.id]})
                </button>
              ))}
            </div>
          </div>
          <div className="p-2 divide-y divide-slate-50">
            {rows.map(({ entity, product }) => (
              <EndpointRow
                key={entity.key}
                entity={entity}
                product={product}
                stat={stats[entity.key]}
                onOpen={() => setSelected({ entity, product })}
                onNavigate={(slug) => navigate(slug)}
              />
            ))}
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Sync status */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm" data-testid="sync-status-panel">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h2 className="font-heading font-bold text-lg text-slate-900">Sync Status</h2>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600"><span className="w-2 h-2 rounded-full bg-emerald-500" />All systems healthy</span>
            </div>
            <div className="p-2">
              {syncRows.map((s) => {
                const a = ACCENT[s.accent];
                const Icon = PRODUCT_ICON[s.id];
                return (
                  <div key={s.id} className="flex items-center gap-3 px-3 py-3">
                    <div className={`w-9 h-9 rounded-xl ${a.soft} flex items-center justify-center flex-shrink-0`}><Icon className={`w-4 h-4 ${a.icon}`} /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-slate-900">{s.name}</p>
                      <p className="text-[11px] text-slate-400">Last {s.status === 'Receiving' ? 'submission' : 'sync'} {fmtShort(s.last)}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${s.status === 'Receiving' ? 'text-amber-600' : 'text-emerald-600'}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" />{s.status}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Integration rules */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm" data-testid="rules-panel">
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="font-heading font-bold text-lg text-slate-900">Integration Rules</h2>
            </div>
            <div className="p-4 space-y-3">
              {INTEGRATION_RULES.map((r, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="text-[11px] font-mono font-bold text-violet-500 mt-0.5">{String(i + 1).padStart(2, '0')}</span>
                  <p className="text-[12.5px] text-slate-700 leading-relaxed">{r}</p>
                </div>
              ))}
            </div>
          </div>

          {/* OAuth note */}
          <div className="bg-slate-900 rounded-2xl px-5 py-4" data-testid="oauth-strip">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-xl bg-violet-500/20 border border-violet-500/40 flex items-center justify-center flex-shrink-0">
                <KeyRound className="w-4 h-4 text-violet-300" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-bold">Shared OAuth 2.0</p>
                <p className="font-mono text-[11px] text-slate-200 break-all">{OAUTH_NOTE.endpoint}</p>
              </div>
            </div>
            <p className="text-[11.5px] text-slate-400 leading-relaxed">{OAUTH_NOTE.detail}</p>
          </div>
        </div>
      </div>

      <EntityModal
        selected={selected}
        stat={selected ? stats[selected.entity.key] : null}
        onClose={() => setSelected(null)}
        onNavigate={(slug) => { setSelected(null); navigate(slug); }}
      />
    </div>
  );
}
